/**
 * Helpers HTTP compartilhados dos módulos financeiros.
 */
import axios from "axios";

export function apiUrl(): string {
  return process.env.REACT_APP_API_URL || "";
}

/** True quando a falha é só "sem registros ainda", não erro de servidor/rede. */
export function isEmptyDataLoadError(error: unknown): boolean {
  let status: number | undefined;
  let message = "";

  if (axios.isAxiosError(error)) {
    status = error.response?.status;
    const data = error.response?.data as Record<string, unknown> | undefined;
    message = String(data?.message || data?.error || error.message || "");
  } else if (error && typeof error === "object" && "response" in error) {
    const resp = (
      error as { response?: { status?: number; data?: Record<string, unknown> } }
    ).response;
    status = resp?.status;
    message = String(resp?.data?.message || resp?.data?.error || "");
  } else {
    return false;
  }

  if (status === 404 || status === 204) return true;
  const lower = message.toLowerCase();
  return (
    lower.includes("não encontrad") ||
    lower.includes("nao encontrad") ||
    lower.includes("not found") ||
    lower.includes("no data") ||
    lower.includes("nenhum") ||
    lower.includes("sem registro") ||
    lower.includes("lista vazia") ||
    lower.includes("empty")
  );
}

/** Extrai arrays de formatos comuns de resposta da API. */
export function extractList(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  const obj = payload as Record<string, unknown>;
  const candidates = [
    obj.itens,
    obj.items,
    obj.data,
    obj.result,
    obj.results,
    obj.records,
    obj.cardapio_itens,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  if (obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)) {
    return extractList(obj.data);
  }
  return [];
}

/** Carrega lista; retorna [] quando ainda não há dados (sem popup). */
export async function fetchListOrEmpty<T>(
  loader: () => Promise<T[]>,
): Promise<T[]> {
  try {
    const result = await loader();
    return Array.isArray(result) ? result : [];
  } catch (error) {
    if (isEmptyDataLoadError(error)) return [];
    throw error;
  }
}

export async function loadAuxiliaryData<T extends unknown[]>(
  loaders: { label: string; load: () => Promise<unknown> }[],
): Promise<T> {
  const results = await Promise.allSettled(loaders.map((l) => l.load()));
  const values: unknown[] = [];

  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    if (result.status === "fulfilled") {
      values.push(result.value);
      continue;
    }
    if (isEmptyDataLoadError(result.reason)) {
      values.push([]);
      continue;
    }
    throw result.reason;
  }

  return values as T;
}
